import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { UserCheck, UserX, Users, Shield, Clock, Mail, KeyRound, Baby, Trash2, Briefcase, PoundSterling } from 'lucide-react';
import { toast } from 'sonner';

export default function UserManagementPage() {
  const { api, isDirector } = useAuth();
  const [pendingUsers, setPendingUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [accessGrants, setAccessGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newRole, setNewRole] = useState('');
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [permissionDialogOpen, setPermissionDialogOpen] = useState(false);
  const [selectedPermissionUser, setSelectedPermissionUser] = useState(null);

  useEffect(() => {
    if (isDirector) {
      fetchData();
    }
  }, [isDirector]);

  const fetchData = async () => {
    try {
      const [pendingRes, staffRes, grantsRes] = await Promise.all([
        api.get('/auth/pending-users'),
        api.get('/staff'),
        api.get('/auth/access-grants').catch(() => ({ data: [] }))
      ]);
      setPendingUsers(pendingRes.data);
      setAllUsers(staffRes.data);
      setAccessGrants(grantsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load user data');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId) => {
    try {
      await api.post(`/auth/approve-user/${userId}`);
      toast.success('User approved successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve user');
    }
  };

  const handleReject = async (userId) => {
    try {
      await api.post(`/auth/reject-user/${userId}`);
      toast.success('User registration rejected');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject user');
    }
  };

  const handleUpdateRole = async () => {
    if (!selectedUser || !newRole) return;
    try {
      const response = await api.put(`/auth/update-user-role/${selectedUser.id}?role=${newRole}`);
      toast.success(response.data?.message || 'User role updated');
      
      // Show additional info if role was changed
      if (response.data?.role_changed) {
        toast.info(`${selectedUser.first_name} will see the new role (${newRole.replace(/_/g, ' ')}) after logout/refresh`, {
          duration: 5000
        });
      }
      
      setRoleDialogOpen(false);
      setSelectedUser(null);
      setNewRole('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const openRoleDialog = (user) => {
    setSelectedUser(user);
    setNewRole(user.role);
    setRoleDialogOpen(true);
  };

  // Check if user has a specific permission grant
  const hasPermissionGrant = (userId, resourceType) => {
    return accessGrants.some(g => g.user_id === userId && g.resource_type === resourceType && g.is_active);
  };

  // Grant permission to a user
  const handleGrantPermission = async (user, resourceType) => {
    try {
      await api.post('/auth/access-grants', {
        user_id: user.id,
        user_email: user.email,
        resource_type: resourceType,
        can_view: true,
        can_edit: true,
        reason: `Granted ${resourceType.replace(/_/g, ' ')} permission by admin`
      });
      toast.success(`Permission granted to ${user.first_name}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to grant permission');
    }
  };

  // Revoke permission from a user
  const handleRevokePermission = async (grantId, userName) => {
    try {
      await api.delete(`/auth/access-grants/${grantId}`);
      toast.success(`Permission revoked from ${userName}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke permission');
    }
  };

  // Open permission dialog
  const openPermissionDialog = (user) => {
    setSelectedPermissionUser(user);
    setPermissionDialogOpen(true);
  };

  const getRoleColor = (role) => {
    const colors = {
      director: 'bg-purple-100 text-purple-700',
      managing_director: 'bg-purple-200 text-purple-800',
      clinical_lead: 'bg-blue-100 text-blue-700',
      therapist: 'bg-teal-100 text-teal-700',
      rehab_assistant: 'bg-cyan-100 text-cyan-700',
      admin: 'bg-slate-100 text-slate-700',
      reception: 'bg-orange-100 text-orange-700',
      finance: 'bg-amber-100 text-amber-700',
      hr: 'bg-pink-100 text-pink-700'
    };
    return colors[role] || 'bg-slate-100 text-slate-700';
  };

  if (!isDirector) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Shield className="w-16 h-16 mx-auto text-slate-300 mb-4" />
          <h2 className="text-xl font-bold text-slate-700">Access Restricted</h2>
          <p className="text-slate-500">Only Directors can access user management.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-[#1e3a5f]">User Management</h1>
        <p className="text-slate-500 mt-1">Approve registrations and manage user roles</p>
      </div>

      {/* Pending Approvals */}
      <Card className="card-icrcl border-amber-200 bg-amber-50/30">
        <CardHeader>
          <CardTitle className="text-lg text-amber-700 flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Pending Approvals ({pendingUsers.length})
          </CardTitle>
          <CardDescription>Users waiting for registration approval</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          ) : pendingUsers.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <UserCheck className="w-12 h-12 mx-auto mb-3 text-green-300" />
              <p>No pending registrations</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Requested Role</TableHead>
                  <TableHead>Discipline</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingUsers.map((user) => (
                  <TableRow key={user.id} className="bg-white">
                    <TableCell className="font-medium">
                      {user.first_name} {user.last_name}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-slate-400" />
                        {user.email}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={getRoleColor(user.role)}>
                        {user.role?.replace(/_/g, ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {user.discipline ? (
                        <Badge variant="outline">
                          {user.discipline.replace(/_/g, ' ')}
                        </Badge>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="text-slate-500 text-sm">
                      {new Date(user.created_at).toLocaleDateString('en-GB')}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => handleApprove(user.id)}
                          data-testid={`approve-${user.id}`}
                        >
                          <UserCheck className="w-4 h-4 mr-1" />
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleReject(user.id)}
                          data-testid={`reject-${user.id}`}
                        >
                          <UserX className="w-4 h-4 mr-1" />
                          Reject
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* All Users */}
      <Card className="card-icrcl">
        <CardHeader>
          <CardTitle className="text-lg text-[#1e3a5f] flex items-center gap-2">
            <Users className="w-5 h-5" />
            All Staff Members ({allUsers.length})
          </CardTitle>
          <CardDescription>Manage roles and permissions</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {allUsers.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No staff members found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="table-icrcl">
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Discipline</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {allUsers.map((user) => (
                  <TableRow key={user.id} className="hover:bg-slate-50">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#1e3a5f] flex items-center justify-center text-white font-medium">
                          {user.first_name?.[0]}{user.last_name?.[0]}
                        </div>
                        <div>
                          <p className="font-medium">{user.first_name} {user.last_name}</p>
                          {user.email === 'admin@icrcl.co.uk' && (
                            <span className="text-xs text-purple-600 font-medium">Main Administrator</span>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600">{user.email}</TableCell>
                    <TableCell>
                      <Badge className={getRoleColor(user.role)}>
                        {user.role?.replace(/_/g, ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {user.discipline ? (
                        <Badge variant="outline">
                          {user.discipline.replace(/_/g, ' ')}
                        </Badge>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <Badge className={user.is_active ? 'badge-active' : 'badge-cancelled'}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {user.email !== 'admin@icrcl.co.uk' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openRoleDialog(user)}
                        >
                          Change Role
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Special Permissions */}
      <Card className="card-icrcl border-blue-200 bg-blue-50/30">
        <CardHeader>
          <CardTitle className="text-lg text-blue-700 flex items-center gap-2">
            <KeyRound className="w-5 h-5" />
            Special Permissions
          </CardTitle>
          <CardDescription>Grant additional permissions to staff members who don't have them by default</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Child Registration Permission */}
            <div className="p-4 bg-white rounded-lg border">
              <div className="flex items-center gap-3 mb-3">
                <Baby className="w-5 h-5 text-blue-600" />
                <div>
                  <h4 className="font-medium text-slate-800">Child Registration Permission</h4>
                  <p className="text-sm text-slate-500">Allow users to register new children and families</p>
                </div>
              </div>
              <p className="text-xs text-slate-400 mb-3">
                Default roles with this permission: Director, Clinical Lead, Admin, Therapist, Reception
              </p>
              
              {/* Users with granted permission */}
              {accessGrants.filter(g => g.resource_type === 'patient_registration' && g.is_active).length > 0 && (
                <div className="mb-3">
                  <p className="text-sm font-medium text-slate-600 mb-2">Users with granted permission:</p>
                  <div className="flex flex-wrap gap-2">
                    {accessGrants
                      .filter(g => g.resource_type === 'patient_registration' && g.is_active)
                      .map(grant => {
                        const grantUser = allUsers.find(u => u.id === grant.user_id);
                        return (
                          <Badge key={grant.id} className="bg-blue-100 text-blue-700 flex items-center gap-1">
                            {grantUser ? `${grantUser.first_name} ${grantUser.last_name}` : grant.user_email}
                            <button
                              onClick={() => handleRevokePermission(grant.id, grantUser?.first_name || 'User')}
                              className="ml-1 hover:text-red-600"
                              title="Revoke permission"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </Badge>
                        );
                      })}
                  </div>
                </div>
              )}
              
              {/* Grant to new user */}
              <div className="flex items-center gap-2">
                <Select onValueChange={(userId) => {
                  const user = allUsers.find(u => u.id === userId);
                  if (user) handleGrantPermission(user, 'patient_registration');
                }}>
                  <SelectTrigger className="w-64">
                    <SelectValue placeholder="Select user to grant permission..." />
                  </SelectTrigger>
                  <SelectContent>
                    {allUsers
                      .filter(u => 
                        // Exclude users who already have permission by default or grant
                        !['director', 'clinical_lead', 'admin', 'therapist', 'reception'].includes(u.role) &&
                        !hasPermissionGrant(u.id, 'patient_registration')
                      )
                      .map(user => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.first_name} {user.last_name} ({user.role?.replace(/_/g, ' ')})
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Business Development Manager Access Control */}
      <Card className="card-icrcl border-purple-200 bg-purple-50/30">
        <CardHeader>
          <CardTitle className="text-lg text-purple-700 flex items-center gap-2">
            <Briefcase className="w-5 h-5" />
            Business Development Access Control
          </CardTitle>
          <CardDescription>
            Grant or revoke access to sensitive areas for Business Development Managers
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* List BDMs */}
            {allUsers.filter(u => u.role === 'business_development').length === 0 ? (
              <p className="text-sm text-slate-500 italic">No Business Development Managers registered</p>
            ) : (
              <>
                {allUsers
                  .filter(u => u.role === 'business_development')
                  .map(bdm => (
                    <div key={bdm.id} className="p-4 bg-white rounded-lg border">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="font-medium text-slate-800">{bdm.first_name} {bdm.last_name}</h4>
                          <p className="text-sm text-slate-500">{bdm.email}</p>
                        </div>
                        <Badge variant="outline" className="bg-purple-100 text-purple-700">
                          Business Development
                        </Badge>
                      </div>
                      
                      <p className="text-xs text-slate-400 mb-3">
                        Default access: Staff Directory, External Professionals, Events, Safeguarding
                      </p>
                      
                      <div className="space-y-3">
                        {/* Clinical Access */}
                        <div className="flex items-center justify-between p-2 bg-slate-50 rounded">
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-slate-500" />
                            <span className="text-sm">Clinical Information (Children & Families)</span>
                          </div>
                          {hasPermissionGrant(bdm.id, 'clinical_access') ? (
                            <Button 
                              size="sm" 
                              variant="destructive"
                              onClick={() => {
                                const grant = accessGrants.find(g => g.user_id === bdm.id && g.resource_type === 'clinical_access');
                                if (grant) handleRevokePermission(grant.id, bdm.first_name);
                              }}
                            >
                              Revoke
                            </Button>
                          ) : (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleGrantPermission(bdm, 'clinical_access')}
                            >
                              Grant Access
                            </Button>
                          )}
                        </div>
                        
                        {/* Finance Access */}
                        <div className="flex items-center justify-between p-2 bg-slate-50 rounded">
                          <div className="flex items-center gap-2">
                            <PoundSterling className="w-4 h-4 text-slate-500" />
                            <span className="text-sm">Finance Information</span>
                          </div>
                          {hasPermissionGrant(bdm.id, 'finance_access') ? (
                            <Button 
                              size="sm" 
                              variant="destructive"
                              onClick={() => {
                                const grant = accessGrants.find(g => g.user_id === bdm.id && g.resource_type === 'finance_access');
                                if (grant) handleRevokePermission(grant.id, bdm.first_name);
                              }}
                            >
                              Revoke
                            </Button>
                          ) : (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleGrantPermission(bdm, 'finance_access')}
                            >
                              Grant Access
                            </Button>
                          )}
                        </div>
                        
                        {/* HR Access */}
                        <div className="flex items-center justify-between p-2 bg-slate-50 rounded">
                          <div className="flex items-center gap-2">
                            <Briefcase className="w-4 h-4 text-slate-500" />
                            <span className="text-sm">HR Information</span>
                          </div>
                          {hasPermissionGrant(bdm.id, 'hr_access') ? (
                            <Button 
                              size="sm" 
                              variant="destructive"
                              onClick={() => {
                                const grant = accessGrants.find(g => g.user_id === bdm.id && g.resource_type === 'hr_access');
                                if (grant) handleRevokePermission(grant.id, bdm.first_name);
                              }}
                            >
                              Revoke
                            </Button>
                          ) : (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleGrantPermission(bdm, 'hr_access')}
                            >
                              Grant Access
                            </Button>
                          )}
                        </div>
                        
                        {/* Audit Logs Access */}
                        <div className="flex items-center justify-between p-2 bg-slate-50 rounded">
                          <div className="flex items-center gap-2">
                            <Shield className="w-4 h-4 text-slate-500" />
                            <span className="text-sm">Audit Logs</span>
                          </div>
                          {hasPermissionGrant(bdm.id, 'audit_access') ? (
                            <Button 
                              size="sm" 
                              variant="destructive"
                              onClick={() => {
                                const grant = accessGrants.find(g => g.user_id === bdm.id && g.resource_type === 'audit_access');
                                if (grant) handleRevokePermission(grant.id, bdm.first_name);
                              }}
                            >
                              Revoke
                            </Button>
                          ) : (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleGrantPermission(bdm, 'audit_access')}
                            >
                              Grant Access
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Role Change Dialog */}
      <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change User Role</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4">
              <div>
                <p className="font-medium">{selectedUser.first_name} {selectedUser.last_name}</p>
                <p className="text-sm text-slate-500">{selectedUser.email}</p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">New Role</label>
                <Select value={newRole} onValueChange={setNewRole}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a role..." />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Leadership */}
                    <SelectItem value="director">Director</SelectItem>
                    <SelectItem value="managing_director">Managing Director</SelectItem>
                    
                    {/* Clinical */}
                    <SelectItem value="clinical_lead">Clinical Lead</SelectItem>
                    <SelectItem value="therapist">Therapist (OT/SLT/PT)</SelectItem>
                    <SelectItem value="rehab_assistant">Rehab Assistant</SelectItem>
                    
                    {/* Administrative */}
                    <SelectItem value="admin">Admin Staff</SelectItem>
                    <SelectItem value="reception">Reception</SelectItem>
                    <SelectItem value="business_development">Business Development Manager</SelectItem>
                    
                    {/* Finance & HR */}
                    <SelectItem value="finance">Finance</SelectItem>
                    <SelectItem value="hr">HR Staff</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateRole} className="bg-[#1e3a5f] hover:bg-[#1e3a5f]/90">
              Update Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
